import * as React from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import type { Moment } from 'moment'
import DatePickerPopup from '@/ui/date/DatePickerPopup'
import { faCalendar, FaIcon } from '@/lib/fa'

const DISPLAY_FORMAT = 'YYYY-MM-DD HH:mm'

// Maska na cyfry: RRRR-MM-DD GG:MM.
const applyMask = (digits: string) => {
  let result = ''
  for (let i = 0; i < digits.length; i++) {
    if (i === 4 || i === 6) result += '-'
    if (i === 8) result += ' '
    if (i === 10) result += ':'
    result += digits[i]
  }
  return result
}

interface TmsDateTimeInputProps {
  value: Moment | null
  onChange: (value: Moment) => void
}

// Kalendarz zmienia tylko dzień - minuty z TMS (np. 13:49) zostają,
// czego nie da DateAndTimeField z siatką co 15 minut.
const TmsDateTimeInput: React.FC<TmsDateTimeInputProps> = ({
  value,
  onChange,
}) => {
  const format = (v: Moment | null) =>
    v && v.isValid() ? v.format(DISPLAY_FORMAT) : ''

  const [text, setText] = React.useState(format(value))
  const [isOpen, setIsOpen] = React.useState(false)
  const [position, setPosition] = React.useState({ top: 0, left: 0 })
  const containerRef = React.useRef<HTMLDivElement>(null)
  const popupRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setText(format(value))
  }, [value])

  // Jak portalowe menu Dropdown: popup idzie za polem przy przewijaniu tabeli.
  React.useEffect(() => {
    if (!isOpen) return undefined
    const place = () => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (rect) setPosition({ top: rect.bottom + 4, left: rect.left })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen) return undefined
    const handler = (e: MouseEvent) => {
      if (
        !containerRef.current?.contains(e.target as Node) &&
        !popupRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  const commit = () => {
    const parsed = moment(text, DISPLAY_FORMAT, true)
    if (parsed.isValid()) {
      if (!value || !parsed.isSame(value, 'minute')) onChange(parsed)
    } else {
      setText(format(value))
    }
  }

  const togglePopup = () => setIsOpen((open) => !open)

  const handlePick = (date: string) => {
    const time = value ? value.format('HH:mm') : '00:00'
    onChange(moment(`${date} ${time}`, DISPLAY_FORMAT))
    setIsOpen(false)
  }

  return (
    <div
      ref={containerRef}
      className="tw-flex tw-min-w-[150px] tw-items-center tw-gap-2 tw-rounded tw-border tw-border-base-300 tw-bg-white tw-px-2 tw-py-1"
    >
      <input
        type="text"
        value={text}
        onChange={(e) =>
          setText(applyMask(e.target.value.replace(/\D/g, '').slice(0, 12)))
        }
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        placeholder="RRRR-MM-DD GG:MM"
        className="tw-w-full tw-min-w-0 tw-bg-transparent tw-text-sm tw-text-content-base focus:tw-outline-none"
      />
      <FaIcon
        icon={faCalendar}
        className="tw-cursor-pointer tw-text-content-base"
        onClick={togglePopup}
      />
      {isOpen &&
        createPortal(
          <div
            ref={popupRef}
            className="tw-fixed tw-z-[9999] tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
            style={{ top: position.top, left: position.left }}
          >
            <DatePickerPopup
              value={value ? value.format('YYYY-MM-DD') : null}
              submit={handlePick}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

export default TmsDateTimeInput
